(() => {
  class BannerWithHotspots extends HTMLElement {
    constructor() {
      super();
      this.tooltips = [];
      this.activeTooltip = null;
      this.boundOnClick = this.onDocumentClick.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.tooltips = Array.from(this.querySelectorAll('.banner-hotspots__tooltip'));
      this.productDots = Array.from(this.querySelectorAll('[data-hotspot-product]'));
      this.infoDots = Array.from(this.querySelectorAll('[data-hotspot-info]'));

      this.productDots.forEach((dot) => {
        dot.addEventListener('click', (e) => this.onProductClick(e, dot));
      });

      this.infoDots.forEach((dot) => {
        dot.addEventListener('click', (e) => this.onInfoClick(e, dot));
      });

      document.addEventListener('click', this.boundOnClick);
      document.addEventListener('keydown', this.boundOnKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundOnClick);
      document.removeEventListener('keydown', this.boundOnKeydown);
    }

    /* Product hotspot → open quick view drawer */
    onProductClick(e, dot) {
      e.preventDefault();
      e.stopPropagation();
      this.closeAllTooltips();

      const handle = dot.getAttribute('data-product-handle');
      if (!handle) return;

      const modal = document.querySelector('#product-quick-add-dialog');
      if (!modal) return;

      const modalContent = modal.querySelector('.base-modal__content');
      if (!modalContent) return;

      fetch('/products/' + handle)
        .then((res) => res.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const productSection = doc.querySelector('.shopify-section.product-section');
          if (!productSection) return;

          const stickyCart = productSection.querySelector('.sticky-addtocart-wrapper');
          if (stickyCart) stickyCart.remove();

          if (typeof HTMLUpdateUtility !== 'undefined' && HTMLUpdateUtility.setInnerHTML) {
            HTMLUpdateUtility.setInnerHTML(modalContent, productSection.outerHTML);
          } else {
            modalContent.innerHTML = productSection.outerHTML;
          }

          if (typeof loadingImages === 'function') loadingImages(modalContent);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          if (modal.show) modal.show(dot);

          const variantsPicker = modal.querySelectorAll('variant-radios, variant-selects');
          variantsPicker.forEach((el) => el.setAttribute('data-update-url', 'false'));

          this.initQuickAddMediaNav(modalContent);
        });
    }

    initQuickAddMediaNav(container) {
      const imageWrapper = container.querySelector('.product-image-on-quick-add');
      if (!imageWrapper) return;

      const items = imageWrapper.querySelectorAll('.quick-add-media-item');
      const prevBtn = imageWrapper.querySelector('.quick-add-media-nav__btn--prev');
      const nextBtn = imageWrapper.querySelector('.quick-add-media-nav__btn--next');
      if (!prevBtn || !nextBtn || items.length <= 1) return;

      const navigate = (direction) => {
        const current = imageWrapper.querySelector('.quick-add-media-item:not(.hidden)');
        if (!current) return;
        const idx = parseInt(current.dataset.quickAddMediaIndex);
        let next = direction === 'next'
          ? (idx + 1 >= items.length ? 0 : idx + 1)
          : (idx - 1 < 0 ? items.length - 1 : idx - 1);

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        current.classList.add('quick-add-fade-out');
        setTimeout(() => {
          current.classList.add('hidden');
          current.classList.remove('quick-add-fade-out');
          const nextItem = imageWrapper.querySelector('[data-quick-add-media-index="' + next + '"]');
          if (nextItem) nextItem.classList.remove('hidden');
        }, 300);
      };

      prevBtn.addEventListener('click', () => navigate('prev'));
      nextBtn.addEventListener('click', () => navigate('next'));
    }

    /* Information hotspot → toggle tooltip */
    onInfoClick(e, dot) {
      e.preventDefault();
      e.stopPropagation();

      const item = dot.closest('.banner-hotspots__hotspot-item');
      const tooltip = item ? item.querySelector('.banner-hotspots__tooltip') : null;
      if (!tooltip) return;

      if (tooltip.classList.contains('banner-hotspots__tooltip--active')) {
        this.closeTooltip(tooltip);
      } else {
        this.closeAllTooltips();
        this.openTooltip(tooltip, item);
      }
    }

    openTooltip(tooltip, item) {
      this.positionTooltip(tooltip, item);
      tooltip.classList.add('banner-hotspots__tooltip--active');
      this.activeTooltip = tooltip;
      this.classList.add('banner-hotspots--tooltip-open');
    }

    closeTooltip(tooltip) {
      tooltip.classList.remove('banner-hotspots__tooltip--active');
      if (this.activeTooltip === tooltip) this.activeTooltip = null;
    }

    closeAllTooltips() {
      this.tooltips.forEach((t) => this.closeTooltip(t));
      this.classList.remove('banner-hotspots--tooltip-open');
    }

    positionTooltip(tooltip, item) {
      tooltip.classList.remove(
        'banner-hotspots__tooltip--below',
        'banner-hotspots__tooltip--align-left',
        'banner-hotspots__tooltip--align-right'
      );

      const banner = this.querySelector('.banner-hotspots__banner');
      if (!banner) return;

      const bannerRect = banner.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const tooltipWidth = window.innerWidth < 750 ? 240 : 280;
      const tooltipHeight = tooltip.scrollHeight || 200;

      /* Vertical: prefer above, fall back to below */
      const spaceAbove = itemRect.top - bannerRect.top;
      if (spaceAbove < tooltipHeight + 24) {
        tooltip.classList.add('banner-hotspots__tooltip--below');
      }

      /* Horizontal: check if tooltip overflows left or right of banner */
      const itemCenterInBanner = itemRect.left - bannerRect.left + itemRect.width / 2;
      const halfTooltip = tooltipWidth / 2;

      if (itemCenterInBanner < halfTooltip + 12) {
        tooltip.classList.add('banner-hotspots__tooltip--align-left');
      } else if (bannerRect.width - itemCenterInBanner < halfTooltip + 12) {
        tooltip.classList.add('banner-hotspots__tooltip--align-right');
      }
    }

    onDocumentClick(e) {
      if (!this.activeTooltip) return;
      if (e.target.closest('.banner-hotspots__tooltip') || e.target.closest('.banner-hotspots__dot')) return;
      this.closeAllTooltips();
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.activeTooltip) {
        this.closeAllTooltips();
      }
    }
  }

  if (!customElements.get('banner-with-hotspots')) {
    customElements.define('banner-with-hotspots', BannerWithHotspots);
  }
})();